import * as signalR from '@microsoft/signalr';
import { authService } from './authService';

/**
 * Conexão SignalR ÚNICA por aba (singleton de módulo).
 *
 * Antes, cada instância do hook useNotifications criava sua própria HubConnection —
 * NotificationCenter + QueryInvalidator (+ PixQrCheckout quando aberto) = 2–3 conexões
 * simultâneas por aba, gerando churn de connect/disconnect no hub.
 *
 * Agora todos os consumidores compartilham UMA conexão via subscribe() com ref-count:
 * - 1º subscriber → inicia a conexão;
 * - último a sair → agenda stop com período de graça (evita thrash em remounts/StrictMode);
 * - handlers de notificação e listeners de estado são conjuntos independentes do ciclo
 *   de vida da conexão (trocar handler NÃO reinicia a conexão).
 *
 * Comportamento preservado do hook antigo:
 * - withAutomaticReconnect com os mesmos delays;
 * - withServerTimeout(90000);
 * - retry manual a cada 10s se o start inicial falhar;
 * - logger com prefixo [SignalR] (Warning+).
 */

const SIGNALR_HUB_URL = import.meta.env.VITE_NOTIFICATIONS_API_URL
  ? `${import.meta.env.VITE_NOTIFICATIONS_API_URL.replace('/api', '')}/hubs/notifications`
  : 'https://notifications.agendario.com.br/hubs/notifications';

const RETRY_DELAYS_MS = [2000, 5000, 10000, 30000, 60000];
const MANUAL_RETRY_MS = 10000;
const STOP_GRACE_MS = 5000;

const signalRLogger = {
  log(logLevel, message) {
    const taggedMessage = `[SignalR] ${message}`;
    // Os erros do SignalR (WebSocket 1006, negotiation 401, reconexão) são TRANSITÓRIOS: o
    // withAutomaticReconnect trata, e o estado real de conexão já é exposto via state listeners/UI.
    // Beaconar cada blip como console.error inundava o card "Erros Frontend" (o console-logger
    // central captura SÓ console.error). Por isso Error/Warning do SignalR vão para console.warn
    // (visível no console, NÃO beaconado). Falha PERSISTENTE aparece pelo estado desconectado, não
    // por um erro de console por tentativa.
    if (logLevel >= signalR.LogLevel.Warning) {
      console.warn(taggedMessage);
    }
  },
};

let connection = null;
let refCount = 0;
let started = false;          // start() já foi disparado (conexão ativa ou reconectando)
let isConnected = false;
let lastError = null;
let stopTimer = null;
let retryTimer = null;

const notificationHandlers = new Set();
const stateListeners = new Set();

function notifyState() {
  const snapshot = { connected: isConnected, error: lastError };
  stateListeners.forEach((listener) => {
    try {
      listener(snapshot);
    } catch (e) {
      console.warn('[SignalR] State listener error:', e?.message);
    }
  });
}

function setState(connected, error) {
  isConnected = connected;
  lastError = error ?? null;
  notifyState();
}

function ensureConnection() {
  if (connection) return connection;

  connection = new signalR.HubConnectionBuilder()
    .withUrl(SIGNALR_HUB_URL, {
      // Lê o token a cada negotiate/reconnect (o hook antigo capturava o token uma
      // única vez no mount — reconexões após renovar login usavam token velho).
      accessTokenFactory: () => authService.getToken(),
    })
    // Forma de ARRAY: a biblioteca espera cada valor e, esgotada a lista, desiste → onclose → retry manual → start()
    // → 401 vira ida ao login. Antes era `nextRetryDelayInMilliseconds: () => RETRY_DELAYS_MS`, que devolvia o ARRAY
    // inteiro: o setTimeout recebia NaN (espera 0) e nunca vinha null (nunca desistia) — uma aba com token vencido
    // martelou o hub com 220 mil negotiate 401 em 23–24/10/2026 (V-057).
    .withAutomaticReconnect(RETRY_DELAYS_MS)
    .withServerTimeout(90000)
    .configureLogging(signalRLogger)
    .build();

  // Dispatcher único → todos os handlers registrados
  connection.on('ReceiveNotification', (notification) => {
    notificationHandlers.forEach((handler) => {
      try {
        handler(notification);
      } catch (e) {
        console.warn('[SignalR] Notification handler error:', e?.message);
      }
    });
  });

  connection.onclose((error) => {
    console.warn('[SignalR] Connection closed:', error?.message);
    setState(false, error?.message || 'Connection closed');
    started = false;
    // Auto-reconnect esgotou (ou queda dura). Se ainda há consumidores, tenta de novo.
    scheduleManualRetry();
  });

  connection.onreconnecting((error) => {
    console.warn('[SignalR] Reconnecting:', error?.message);
    setState(false, error?.message || null);
  });

  connection.onreconnected((connectionId) => {
    console.log('[SignalR] Reconnected:', connectionId);
    setState(true, null);
  });

  return connection;
}

function scheduleManualRetry() {
  if (retryTimer || refCount === 0) return;
  retryTimer = setTimeout(() => {
    retryTimer = null;
    if (refCount > 0) {
      console.log('[SignalR] Attempting manual reconnect...');
      startConnection();
    }
  }, MANUAL_RETRY_MS);
}

async function startConnection() {
  if (started || refCount === 0) return;

  const token = authService.getToken();
  if (!token) {
    console.warn('[SignalR] No token available, will retry later');
    scheduleManualRetry();
    return;
  }

  const conn = ensureConnection();
  // start() só é válido a partir do estado Disconnected
  if (conn.state !== signalR.HubConnectionState.Disconnected) return;

  started = true;
  try {
    await conn.start();
    console.log('[SignalR] Connected successfully');
    setState(true, null);
  } catch (error) {
    console.warn('[SignalR] Connection failed:', error?.message);
    started = false;
    setState(false, error?.message);

    // 401 no negotiate = credencial vencida, nao queda de rede: insistir nao resolve, so gera
    // rajada (1.931 tentativas em 3 min, medido em PRD 16/09). Quem resolve e a reautenticacao —
    // e ela e transparente: o Login, com a sessao viva no cookie dele, devolve um JWT novo sem
    // perguntar nada. O guard do authService garante um unico redirecionamento.
    const mensagemDoErro = String(error?.message || '');
    const credencialVencida = error?.statusCode === 401
      || mensagemDoErro.includes('401')
      || mensagemDoErro.toLowerCase().includes('unauthorized');
    if (credencialVencida) {
      const caminhoAtual = window.location.pathname + window.location.search;
      authService.redirectToLogin(caminhoAtual);
      return;
    }

    scheduleManualRetry();
  }
}

function stopConnection() {
  if (!connection) return;
  const conn = connection;
  started = false;
  setState(false, null);
  conn.stop().catch((err) => {
    console.warn('[SignalR] Error stopping connection:', err?.message);
  });
}

/**
 * Registra um consumidor da conexão compartilhada.
 * @param {{ onNotification?: Function, onStateChange?: Function }} opts
 * @returns {Function} unsubscribe
 */
export function subscribeSignalR({ onNotification, onStateChange } = {}) {
  refCount++;
  if (stopTimer) {
    clearTimeout(stopTimer);
    stopTimer = null;
  }

  if (onNotification) notificationHandlers.add(onNotification);
  if (onStateChange) {
    stateListeners.add(onStateChange);
    // Entrega o estado atual imediatamente (consumidor pode montar após a conexão)
    try {
      onStateChange({ connected: isConnected, error: lastError });
    } catch { /* noop */ }
  }

  startConnection();

  let unsubscribed = false;
  return function unsubscribe() {
    if (unsubscribed) return;
    unsubscribed = true;

    if (onNotification) notificationHandlers.delete(onNotification);
    if (onStateChange) stateListeners.delete(onStateChange);
    refCount = Math.max(0, refCount - 1);

    // Último consumidor saiu → para com período de graça (remounts do React/StrictMode
    // não devem derrubar e recriar a conexão).
    if (refCount === 0) {
      if (retryTimer) {
        clearTimeout(retryTimer);
        retryTimer = null;
      }
      stopTimer = setTimeout(() => {
        stopTimer = null;
        if (refCount === 0) stopConnection();
      }, STOP_GRACE_MS);
    }
  };
}

/** Conexão subjacente (pode ser null antes do primeiro subscribe). */
export function getSignalRConnection() {
  return connection;
}
